import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNotificationStats, NotificationStats } from '../hooks/useNotifications';

interface NotificationContextValue {
  stats: NotificationStats | null;
  loading: boolean;
  error: any;
  isNotificationDrawerOpen: boolean;
  openNotificationDrawer: () => void;
  closeNotificationDrawer: () => void;
  toggleNotificationDrawer: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const { data, loading, error } = useNotificationStats();

  const openNotificationDrawer = () => setIsNotificationDrawerOpen(true);
  const closeNotificationDrawer = () => setIsNotificationDrawerOpen(false);
  const toggleNotificationDrawer = () => setIsNotificationDrawerOpen(prev => !prev);

  const value: NotificationContextValue = {
    stats: data?.notificationStats || null,
    loading,
    error,
    isNotificationDrawerOpen,
    openNotificationDrawer,
    closeNotificationDrawer,
    toggleNotificationDrawer
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};
